import { CheckIcon, ClockIcon } from 'assets/icons';
import SummaryCell from 'components/crm/ui/SummaryCell';
import { doneText, dueText, isLate } from 'helper/taskDates';
import { durationLabel } from 'helper/timeLabels';
import useText from 'hooks/useText';
import { CircleDashed } from 'lucide-react';
import TaskDescriptionField from './TaskDescriptionField';
import TaskDueField from './TaskDueField';
import TaskLinksInlineField from './TaskLinksInlineField';

/**
 * The « Détail » tab of a task: the summary of the appointment's panel, its
 * due day and hour (red when late) beside its state (done, and when, or still
 * to do); every record it is linked to, its
 * description. A task has a single kind: no type badge, no e-mail to send. `openerOf(link)`: what opens a
 * linked record, or null. `editInPlace`: a click on the due day or on the
 * links changes them where they are (`onSave(fields)` resolves false on a
 * refusal); `canEdit`: the description is written in place. `linkOptions`:
 * the records that can be linked (helper/taskLinks `linkOptionsOf`).
 */
const TaskDetailTab = ({ task, openerOf, onSave, canEdit = true, editInPlace = false, linkOptions = [] }) => {
  const { text } = useText('tasks');
  const now = new Date();
  const dueCell = (dueOn) => {
    const shown = { ...task, dueOn };
    const late = isLate(shown, now);
    const when = task.dueTime
      ? `${dueText(shown, now)}, ${task.dueTime.slice(0, 5)} · ${durationLabel(task.durationMin ?? 15)}`
      : dueText(shown, now);
    return (
      <SummaryCell
        icon={<ClockIcon wh={16} color="currentColor" />}
        iconClass={late ? 'text-red-600' : undefined}
        label={text('dateTimeLabel')}
        value={when}
        valueClass={late ? 'text-red-600' : undefined}
      />
    );
  };
  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 rounded-xl border border-gray-200">
        <TaskDueField
          key={`${task.id}-due`}
          dueOn={task.dueOn}
          canEdit={editInPlace}
          onSave={onSave}
          renderCell={dueCell}
          className="min-w-0 rounded-l-xl border-r border-gray-200"
        />
        {task.doneAt ? (
          <SummaryCell
            icon={<CheckIcon w={14} h={11} color="currentColor" />}
            iconClass="text-green-700"
            label="État"
            value={doneText(task, now)}
            valueClass="text-green-700"
          />
        ) : (
          <SummaryCell icon={<CircleDashed size={16} aria-hidden="true" />} label="État" value="À faire" />
        )}
      </div>
      <TaskLinksInlineField
        key={`${task.id}-links`}
        task={task}
        openerOf={openerOf}
        options={linkOptions}
        canEdit={editInPlace}
        onSave={onSave}
      />
      <TaskDescriptionField
        key={task.id}
        initial={task.note ?? ''}
        onSave={(note) => onSave({ note })}
        canEdit={canEdit}
      />
    </div>
  );
};

export default TaskDetailTab;
